import { gql, useMutation } from "@apollo/client";
import { Mutation, MutationInitUserResetPasswordArgs } from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Button, Card, Stack, Typography } from "@mui/material";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import VALIDATION from "helpers/validation";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";

const INIT_USER_RESET_PASSWORD_MUTATION = gql`
  mutation initUserResetPassword($email: String!) {
    initUserResetPassword(email: $email)
  }
`;

type ForgotPasswordFormValues = {
  email: string;
};

const FIELD_LABEL_PROPS = {
  variant: "body16rg" as const,
  sx: {
    color: "base.500",
    fontWeight: 600,
    mb: 0.5,
  },
};

const ForgotPasswordPage = () => {
  const { addToast } = useContext(ToastContext);
  const [sentEmail, setSentEmail] = useState("");
  const form = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      email: "",
    },
  });
  const { handleSubmit } = form;

  const [initUserResetPassword, { loading }] = useMutation<
    Pick<Mutation, "initUserResetPassword">,
    MutationInitUserResetPasswordArgs
  >(INIT_USER_RESET_PASSWORD_MUTATION);

  const onSubmit = async (formData: ForgotPasswordFormValues) => {
    try {
      const result = await initUserResetPassword({
        variables: {
          email: formData.email.trim(),
        },
      });

      if (!result.data?.initUserResetPassword) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Не удалось отправить письмо для сброса пароля" });
        return;
      }

      setSentEmail(formData.email.trim());
    } catch (error) {
      parseApolloError(error, {
        addToast,
        defaultText: "Не удалось отправить письмо для сброса пароля",
      });
    }
  };

  if (sentEmail) {
    return (
      <Card
        variant='outlined'
        sx={{ borderRadius: 3, p: { xs: 3, sm: 4 } }}>
        <Stack
          spacing={3}
          alignItems='flex-start'>
          <Stack spacing={1}>
            <Typography variant='h28'>Письмо отправлено</Typography>
            <Typography
              variant='body16rg'
              sx={{ color: "text.secondary", maxWidth: 460 }}>
              Мы отправили письмо со ссылкой для смены пароля на <strong>{sentEmail}</strong>. Перейдите по ссылке из
              письма, чтобы задать новый пароль.
            </Typography>
          </Stack>

          <Button
            component={RouterLink}
            to={`/${ROUTES.LOGIN}`}
            variant='text'
            size='large'
            sx={{ p: 0 }}>
            На страницу входа
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack spacing={3.5}>
      <Stack spacing={1}>
        <Typography variant='h28'>Восстановление пароля</Typography>
        <Typography
          variant='body16rg'
          sx={{ color: "text.secondary", maxWidth: 460 }}>
          Укажите почту, привязанную к аккаунту. Отправим письмо со ссылкой для смены пароля.
        </Typography>
      </Stack>

      <Form
        form={form}
        onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <TextFieldControl
            name='email'
            label='Email'
            type='email'
            variant='outlined'
            labelTypographyProps={FIELD_LABEL_PROPS}
            placeholder='you@company.ru'
            rules={{
              required: true,
              ...VALIDATION.emailFull,
            }}
          />

          <LoadingButton
            type='submit'
            loading={loading}
            variant='contained'
            size='large'>
            Отправить письмо
          </LoadingButton>

          <Button
            component={RouterLink}
            to={`/${ROUTES.LOGIN}`}
            variant='text'
            size='large'>
            Вернуться ко входу
          </Button>
        </Stack>
      </Form>
    </Stack>
  );
};

export default ForgotPasswordPage;
